import { useState } from "react";
import { api, apiError, BACKEND } from "../lib/api";
import { FileUpload } from "./FileUpload";
import { ReceiptScanner } from "./ReceiptScanner";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { FileText, Camera, Eye, RefreshCw, Trash2, Paperclip } from "lucide-react";

// value: { receipt_path, receipt_name, receipt_content_type, receipt_file_id? } | null
export function ReceiptField({ value, onChange, expenseId }) {
  const [scanOpen, setScanOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const has = !!value?.receipt_path;
  const isImage = (value?.receipt_content_type || "").startsWith("image/");

  const applyUpload = (f) => {
    onChange({
      receipt_path: f.storage_path, receipt_name: f.original_filename,
      receipt_content_type: f.content_type, receipt_file_id: f.file_id,
    });
    setReplacing(false);
  };

  const remove = async () => {
    setRemoving(true);
    try {
      if (value?.receipt_file_id) {
        await api.delete(`/files/${value.receipt_file_id}`);
      } else if (expenseId) {
        await api.delete(`/expenses/${expenseId}/receipt`);
      }
      onChange(null);
      toast.success("Receipt removed");
    } catch (e) { toast.error(apiError(e)); }
    setRemoving(false);
  };

  const Sources = () => (
    <div className="grid grid-cols-2 gap-2">
      <Button type="button" variant="outline" data-testid="receipt-scan" onClick={() => setScanOpen(true)} className="h-11 rounded-xl">
        <Camera size={16} className="mr-1.5" /> Scan / Photo
      </Button>
      <FileUpload testId="receipt-choose" accept="image/*,application/pdf" label="Choose file" onUploaded={applyUpload} />
    </div>
  );

  return (
    <div className="mt-1.5">
      {has && !replacing ? (
        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 space-y-3" data-testid="receipt-current">
          <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
            {isImage ? (
              <img src={`${BACKEND}/api/files/${value.receipt_path}`} alt="receipt" className="h-10 w-10 rounded-md object-cover border border-slate-200 shrink-0" />
            ) : (
              <span className="h-10 w-10 rounded-md bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0"><FileText size={18} /></span>
            )}
            <span className="truncate">{value.receipt_name || "Receipt"}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <a href={`${BACKEND}/api/files/${value.receipt_path}`} target="_blank" rel="noreferrer" data-testid="receipt-view"
              className="h-9 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium inline-flex items-center justify-center gap-1 df-pressable">
              <Eye size={14} /> View
            </a>
            <button type="button" data-testid="receipt-replace" onClick={() => setReplacing(true)}
              className="h-9 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium inline-flex items-center justify-center gap-1 df-pressable">
              <RefreshCw size={14} /> Replace
            </button>
            <button type="button" data-testid="receipt-remove" onClick={remove} disabled={removing}
              className="h-9 rounded-lg border border-red-200 bg-white text-red-500 text-xs font-medium inline-flex items-center justify-center gap-1 df-pressable">
              <Trash2 size={14} /> Remove
            </button>
          </div>
        </div>
      ) : replacing ? (
        <div className="space-y-2">
          <Sources />
          <button type="button" onClick={() => setReplacing(false)} className="text-xs text-slate-400 df-pressable">Cancel replace</button>
        </div>
      ) : (
        <div className="space-y-2">
          <Sources />
          <p className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Paperclip size={11} /> Scanned photos are saved as a PDF. Images and PDFs are also accepted.</p>
        </div>
      )}
      <ReceiptScanner open={scanOpen} onClose={() => setScanOpen(false)} onDone={applyUpload} />
    </div>
  );
}
